import { useEffect, useRef } from 'react';

interface HeadlineProps {
  text: string;
  className?: string;
  /** Words rendered in the engine gradient rather than the ink gradient. */
  highlight?: string[];
  as?: 'h1' | 'h2' | 'p' | 'span';
}

/**
 * Reveals a headline in one motion: the type is unmasked left to right while a
 * thin accent rule travels along the leading edge, the way a value prints onto
 * a terminal row.
 *
 * Deliberately not a per-word stagger. Staggering makes the reader wait on the
 * end of the sentence, and the delay compounds with the length of the line —
 * the longest, most important headlines were the slowest to arrive. This is a
 * single ~700ms sweep regardless of how many words there are.
 *
 * The whole string stays in the DOM as one uninterrupted text node, so screen
 * readers and crawlers read a normal sentence.
 */
export function Headline({ text, className, highlight = [], as: Tag = 'h2' }: HeadlineProps) {
  const ref = useRef<HTMLElement>(null);
  const wanted = new Set(highlight.map((w) => w.toLowerCase().replace(/[.,]/g, '')));

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (!('IntersectionObserver' in window)) {
      el.setAttribute('data-headline', 'in');
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.setAttribute('data-headline', 'in');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Split only to colour the highlighted words; spacing is preserved verbatim.
  const parts = text.split(' ');

  return (
    <Tag
      ref={ref as React.Ref<HTMLHeadingElement>}
      className={`headline ${className ?? ''}`}
      data-headline=""
    >
      <span className="headline__text">
        {parts.map((word, i) => {
          const key = word.toLowerCase().replace(/[.,]/g, '');
          const node = wanted.has(key) ? (
            <span key={i} className="gradient-text">
              {word}
            </span>
          ) : (
            word
          );
          return i < parts.length - 1 ? [node, ' '] : node;
        })}
      </span>
      {/* The rule travels past the right edge to exit, so it needs its own
          clipping box — unclipped it widened the page. */}
      <span className="headline__scanWrap" aria-hidden="true">
        <span className="headline__scan" />
      </span>
    </Tag>
  );
}
